/**
 * Tokens visuales del sistema de Claude Design (Login / Dashboard /
 * FacturasEmitidas / ArchivoHistorico / Clientes).
 *
 * Vivian en features/home/components/InvoiceTableAtoms.tsx, que los sigue
 * re-exportando para no tocar las pantallas que ya los importaban. Se movieron
 * aca cuando Clientes los necesito: una pantalla de otro feature no deberia
 * depender de features/home para usar la misma tarjeta o el mismo boton.
 *
 * Solo presentacion: ninguno decide que datos se muestran.
 */

/** Tarjeta base: borde fino + sombra doble muy corta. */
export const CARD =
  'rounded-[14px] border border-[#ececec] bg-white shadow-[0_1px_2px_rgba(10,34,51,.03),0_8px_20px_-10px_rgba(10,34,51,.10)] dark:border-secondary-700 dark:bg-secondary-800';

/** Rotulo de campo / de metrica: mayusculas con tracking ancho. */
export const FIELD_LABEL = 'text-[12px] font-bold uppercase tracking-[0.16em] text-secondary-500';

/** Input / select de una linea. */
export const FIELD =
  'w-full rounded-[10px] border border-[#ececec] bg-white px-3.5 py-[11px] text-[14px] font-medium text-ink-850 outline-none transition-colors placeholder:font-normal placeholder:text-secondary-400 hover:border-secondary-300 focus:border-secondary-400 focus:ring-[3px] focus:ring-secondary-400/[.14] dark:border-secondary-600 dark:bg-secondary-800 dark:text-white';

/** Accion primaria, navy con barrido de luz al hover (.cta-sheen en index.css). */
export const CTA =
  'cta-sheen inline-flex items-center gap-2 rounded-[10px] bg-ink-850 px-[18px] py-[11px] text-[14px] font-semibold text-white transition-colors hover:bg-[#142d40] disabled:cursor-not-allowed disabled:opacity-60';

/** Accion secundaria, blanca con borde. */
export const GHOST =
  'inline-flex items-center gap-2 rounded-[10px] border border-[#ececec] bg-white px-4 py-[11px] text-[14px] font-medium text-ink-850 transition-colors hover:border-secondary-300 dark:border-secondary-600 dark:bg-secondary-800 dark:text-white';

/** Boton de paginacion (.page-btn del diseño). */
export const PAGE_BTN =
  'inline-flex h-8 min-w-[32px] items-center justify-center rounded-lg border border-[#ececec] bg-white px-2 text-[14px] font-medium text-secondary-500 transition-colors hover:border-secondary-300 hover:text-ink-850 disabled:cursor-not-allowed disabled:opacity-40 dark:border-secondary-600 dark:bg-secondary-800';

/**
 * Boton de icono de una fila (.icon-btn del diseño). `danger` es la variante
 * roja del boton de eliminar.
 */
export const ICON_BTN =
  'inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-secondary-400 transition-colors hover:border-[#ececec] hover:bg-[#f6f6f7] hover:text-ink-850 dark:hover:border-secondary-600 dark:hover:bg-secondary-700 dark:hover:text-white';
export const ICON_BTN_DANGER =
  'inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-secondary-400 transition-colors hover:border-[#b8503a]/20 hover:bg-[#b8503a]/[.06] hover:text-[#a24030]';

/**
 * Rotulo de metrica en mono, con tracking muy ancho. Es el encabezado de las
 * tarjetas de resumen (Clientes, Configuración).
 */
export const METRIC_LABEL =
  'font-mono text-[11px] uppercase tracking-[0.24em] text-secondary-400';

/**
 * Numeracion de seccion del diseño de Configuración (`01 · GENERAL`). Ordena
 * pantallas largas sin agregar otro nivel de titulo.
 */
export const SECTION_NUM =
  'font-mono text-[12px] font-bold tracking-[0.12em] text-secondary-400';

/** Titulo de seccion dentro de una tarjeta. */
export const SECTION_TITLE =
  'text-xl font-extrabold tracking-[-0.02em] text-ink-850 dark:text-white';

/** Descripcion bajo el titulo de seccion. */
export const SECTION_DESC = 'text-[14px] leading-[1.55] text-secondary-500 dark:text-secondary-400';

/** Rotulo de campo de formulario (.field-label del diseño). */
export const FORM_LABEL = 'text-xs font-semibold text-secondary-500 dark:text-secondary-400';

/** Texto de ayuda bajo un campo (.field-hint del diseño). */
export const FIELD_HINT = 'text-[12px] text-secondary-400';

/**
 * Separador punteado del diseño. Se usa entre filas de datos tipo recibo.
 * Va como estilo inline porque es un repeating-linear-gradient.
 */
export const DASHED_RULE_STYLE = {
  background: 'repeating-linear-gradient(90deg, #e2e8f0 0 4px, transparent 4px 8px)',
} as const;

/** Zona de carga de archivos (.drop del diseño). */
export const DROPZONE =
  'relative block cursor-pointer overflow-hidden rounded-[14px] border-[1.5px] border-dashed border-secondary-300 bg-secondary-50 px-5 py-8 text-center transition-colors hover:border-teal-500 hover:bg-teal-500/[.03] dark:border-secondary-600 dark:bg-secondary-900';

/** Bloque destacado dentro de una tarjeta (fondo apenas mas frio que el blanco). */
export const INSET_PANEL =
  'rounded-xl border border-[#eef0f2] bg-[#fbfbfc] dark:border-secondary-700 dark:bg-secondary-900';

/**
 * Cuadricula de una fila de ajuste (.setting del diseño): icono, texto y
 * control a la derecha.
 */
export const SETTING_ROW = 'grid grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-3.5';

/** Baldosa de icono de 40px de una fila de ajuste. */
export const SETTING_ICON =
  'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]';

/* ------------------------------------------------------------------ *
 * Modales (Claude Design · NuevoProductoModal.dc.html / EditarSucursalModal.dc.html)
 *
 * El diseño trata el modal como una hoja con tres bandas: cabecera con
 * baldosa de icono en degradado + kicker + titulo grande, cuerpo con scroll
 * propio, y pie gris con la ayuda a la izquierda y las acciones a la derecha.
 * Radio 20px y una sombra larga y suave lo despegan del backdrop navy.
 *
 * Estan aca —y no solo dentro de Modal.tsx— porque varias pantallas montan su
 * propio <Dialog> de headlessui en vez de usar el shell compartido; asi todas
 * pueden vestirse igual sin reescribir su logica.
 * ------------------------------------------------------------------ */

/** Velo del modal: navy translucido con un blur minimo. */
export const MODAL_BACKDROP = 'fixed inset-0 bg-[#0a2233]/55 backdrop-blur-[3px]';

/** Hoja del modal. */
export const MODAL_PANEL =
  'w-full transform overflow-hidden rounded-[20px] bg-white text-left align-middle font-display shadow-[0_40px_100px_-20px_rgba(10,34,51,.4)] transition-all dark:bg-secondary-800';

/** Banda de cabecera, cerrada con la regla dasheada del sistema. */
export const MODAL_HEAD =
  'relative border-b border-dashed border-[#eef0f2] px-9 py-[26px] dark:border-secondary-700';

/** Banda de cuerpo con scroll propio. */
export const MODAL_BODY = 'overflow-y-auto px-9 py-7';

/** Banda de pie: fondo apenas mas frio, ayuda a la izquierda y acciones a la derecha. */
export const MODAL_FOOT =
  'flex flex-wrap items-center justify-between gap-3 border-t border-[#f0f0f0] bg-[#fafafa] px-9 py-[18px] dark:border-secondary-700 dark:bg-secondary-900/40';

/** Baldosa de icono de la cabecera: degradado teal → purpura, icono blanco. */
export const MODAL_ICON_TILE =
  'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] bg-gradient-to-br from-teal-500 to-[#6b239e] text-white [&_svg]:h-[18px] [&_svg]:w-[18px]';

/** Kicker mono/uppercase encima del titulo. */
export const MODAL_KICKER =
  'font-mono text-[11px] font-medium uppercase tracking-[0.24em] text-secondary-400';

/** Titulo del modal. */
export const MODAL_TITLE =
  'text-[22px] font-extrabold leading-tight tracking-[-0.02em] text-ink-850 dark:text-white';

/** Boton de cierre: baldosa con borde que se invierte a navy al hover. */
export const MODAL_CLOSE_X =
  'inline-flex h-[34px] w-[34px] items-center justify-center rounded-[10px] border border-[#ececec] bg-[#fafafa] text-secondary-500 transition-colors hover:border-ink-850 hover:bg-ink-850 hover:text-white dark:border-secondary-600 dark:bg-secondary-900 dark:text-secondary-300 dark:hover:border-white dark:hover:bg-white dark:hover:text-ink-850';

/**
 * Bloque destacado con degradado teal→purpura muy lavado (.fiscal-callout /
 * la tira de "VISTA" del modal de producto).
 */
export const MODAL_CALLOUT =
  'rounded-[14px] border border-teal-500/20 bg-gradient-to-br from-teal-500/[.06] to-[#6b239e]/[.03] dark:border-teal-500/20 dark:from-teal-500/[.08] dark:to-[#6b239e]/[.05]';

/**
 * "Skin" del diseño para un bloque de formulario armado con <Input> / <Select>.
 *
 * Se aplica al contenedor y viste los hijos por selector de descendencia, en vez
 * de tocar el className de cada control: los componentes compartidos conservan
 * sus props (label, error, required, maxLength) y su comportamiento, y solo
 * cambia el aspecto. La definicion esta en src/styles/index.css (.form-skin),
 * que es donde ya viven los estilos del diseño que Tailwind no expresa bien.
 */
export const FORM_SKIN = 'form-skin';

/**
 * Resalte de fila al pasar el mouse, compartido por todos los listados.
 *
 * Antes cada lista usaba un blanco casi invisible (#fafbfc / #fbfbfc) y el
 * hover no se percibia. Ahora es un tinte teal de marca mas una barra de 2px a
 * la izquierda (via inset shadow, que no desplaza el contenido como lo haria un
 * border), asi la fila bajo el cursor se lee como "seleccionada".
 */
export const ROW_HOVER =
  'transition-colors hover:bg-teal-500/[.07] hover:shadow-[inset_2px_0_0_#12c4b2] dark:hover:bg-teal-500/[.12]';

/**
 * Tonos de tarjeta compartidos por el dashboard y las franjas de resumen de los
 * listados. Las tarjetas eran todas blancas sobre un lienzo casi blanco y las
 * pantallas se leian planas: no habia nada que distinguiera una tarjeta de
 * dinero de una de errores.
 *
 * El tono se elige por SIGNIFICADO del dato, no por posicion:
 *   teal   = dinero, aprobadas, totales positivos
 *   amber  = pendientes / en proceso
 *   red    = errores, rechazadas, anuladas
 *   violet = clientes, catalogo, composicion
 *   navy   = totales y conteos neutros
 *
 * `WASH` es un degradado muy tenue desde la esquina (5-8%), no un fondo de
 * color: los numeros conservan el mismo contraste. `BAR` es la franja de acento
 * superior de 3px.
 */
export type CardTone = 'neutral' | 'teal' | 'amber' | 'red' | 'violet';

/**
 * Solo el lavado, sin borde. Lo usan las franjas de resumen y las tarjetas que
 * ya traen su propio borde (CARD).
 *
 * Es un radial con tamaño en PIXELES, no un lineal `to-br`: el lineal reparte el
 * color a lo largo de toda la tarjeta, asi que en las tarjetas grandes (Clientes,
 * Productos) el tinte se diluia hasta no verse, mientras en las chicas se notaba.
 * Con un radial de 340x240px anclado en la esquina, el lavado tiene la misma
 * intensidad sin importar el tamaño de la tarjeta.
 */
export const TONE_SURFACE: Record<CardTone, string> = {
  neutral:
    'bg-white dark:bg-secondary-800 [background-image:radial-gradient(340px_240px_at_0%_0%,rgba(10,34,51,0.13),transparent_70%)] dark:[background-image:radial-gradient(340px_240px_at_0%_0%,rgba(255,255,255,0.10),transparent_70%)]',
  teal:
    'bg-white dark:bg-secondary-800 [background-image:radial-gradient(340px_240px_at_0%_0%,rgba(18,196,178,0.18),transparent_70%)] dark:[background-image:radial-gradient(340px_240px_at_0%_0%,rgba(18,196,178,0.26),transparent_70%)]',
  amber:
    'bg-white dark:bg-secondary-800 [background-image:radial-gradient(340px_240px_at_0%_0%,rgba(245,153,29,0.18),transparent_70%)] dark:[background-image:radial-gradient(340px_240px_at_0%_0%,rgba(245,153,29,0.26),transparent_70%)]',
  red:
    'bg-white dark:bg-secondary-800 [background-image:radial-gradient(340px_240px_at_0%_0%,rgba(184,80,58,0.16),transparent_70%)] dark:[background-image:radial-gradient(340px_240px_at_0%_0%,rgba(184,80,58,0.26),transparent_70%)]',
  violet:
    'bg-white dark:bg-secondary-800 [background-image:radial-gradient(340px_240px_at_0%_0%,rgba(107,35,158,0.16),transparent_70%)] dark:[background-image:radial-gradient(340px_240px_at_0%_0%,rgba(107,35,158,0.26),transparent_70%)]',
};

/** Borde al tono, para tarjetas sueltas. */
export const TONE_BORDER: Record<CardTone, string> = {
  neutral: 'border-[#0a2233]/[.12] dark:border-secondary-700',
  teal: 'border-teal-500/25 dark:border-teal-500/30',
  amber: 'border-accent-500/30 dark:border-accent-500/30',
  red: 'border-[#b8503a]/25 dark:border-[#b8503a]/30',
  violet: 'border-[#6b239e]/25 dark:border-[#6b239e]/30',
};

/** Degradado + borde: para tarjetas sueltas como los KPI del dashboard. */
export const TONE_WASH: Record<CardTone, string> = {
  neutral: `${TONE_SURFACE.neutral} ${TONE_BORDER.neutral}`,
  teal: `${TONE_SURFACE.teal} ${TONE_BORDER.teal}`,
  amber: `${TONE_SURFACE.amber} ${TONE_BORDER.amber}`,
  red: `${TONE_SURFACE.red} ${TONE_BORDER.red}`,
  violet: `${TONE_SURFACE.violet} ${TONE_BORDER.violet}`,
};

export const TONE_BAR: Record<CardTone, string> = {
  neutral: 'bg-[#0a2233] dark:bg-white/60',
  teal: 'bg-teal-500',
  amber: 'bg-accent-500',
  red: 'bg-[#b8503a]',
  violet: 'bg-[#6b239e]',
};

/* ------------------------------------------------------------------ *
 * Estado de factura (StatusChip de InvoiceTableAtoms, resumen y filtro
 * de Estado de las pantallas de Facturas Emitidas / Archivo Histórico).
 *
 * Son los 4 estados REALES del negocio ante la DGI, no los 6
 * eInvoiceStatus granulares que expone Aludra: "Autorizado uso de la FE"
 * agrupa pendiente + procesando + autorizado (códigos 2, 3, 4) porque son
 * el mismo trámite en curso, no 3 situaciones que el usuario necesite
 * diferenciar a simple vista.
 *
 * Fuente única de color: antes el chip de la tabla (código por código)
 * mezclaba un fondo con `accent-500` — variable de marca por tenant, no
 * un hex fijo — con un texto fijo `#d17a12`, así que en un tenant cuyo
 * color de marca no fuera anaranjado el chip mostraba un fondo de un color
 * y un texto de otro: el bug de "los colores no salen bien". Ahora los 3
 * lugares (chip de tabla, barra de resumen, filtro de Estado) leen de
 * aca, siempre en hex fijo.
 * ------------------------------------------------------------------ */
export type InvoiceStatusGroupKey = 'processed' | 'authorized' | 'error' | 'cancelled';

export interface InvoiceStatusGroup {
  key: InvoiceStatusGroupKey;
  label: string;
  /** Códigos `eInvoiceStatus` de Aludra que caen en este estado real. */
  codes: number[];
  /** Tono saturado, para el dot y la barra/segmento. */
  bar: string;
  /** Tono legible sobre blanco, para texto y números. */
  text: string;
}

export const INVOICE_STATUS_GROUPS: InvoiceStatusGroup[] = [
  { key: 'processed', label: 'Procesada', codes: [0], bar: '#12c4b2', text: '#0aa294' },
  { key: 'authorized', label: 'Autorizado uso de la FE', codes: [2, 3, 4], bar: '#fada51', text: '#a16207' },
  { key: 'error', label: 'Error', codes: [1], bar: '#b8503a', text: '#a24030' },
  { key: 'cancelled', label: 'Anulada', codes: [5], bar: '#94a3b8', text: '#64748b' },
];

const FALLBACK_STATUS_COLOR = { bar: '#94a3b8', text: '#64748b' };

/** Resuelve el color del estado real al que pertenece un eInvoiceStatus puntual. */
export function getInvoiceStatusColor(eInvoiceStatus: number): { bar: string; text: string } {
  const group = INVOICE_STATUS_GROUPS.find((g) => g.codes.includes(eInvoiceStatus));
  return group ? { bar: group.bar, text: group.text } : FALLBACK_STATUS_COLOR;
}
